import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, DatePicker, Select, Button, Spin, Typography, Divider, Dropdown, Menu } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, FunnelChart, Funnel, LabelList } from 'recharts';
import { CalendarOutlined, TrophyOutlined, ClockCircleOutlined, UserOutlined, CheckCircleOutlined, ExclamationCircleOutlined, DownloadOutlined, FilePdfOutlined, FileOutlined } from '@ant-design/icons';
import { axiosInstant } from '../../../api';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
// @ts-ignore
import { saveAs } from 'file-saver';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

interface HiringMetrics {
  metrics: {
    totalApplications: number;
    totalHired: number;
    totalRejected: number;
    totalPending: number;
    totalShortlisted: number;
    totalInterviewing: number;
    totalOffersPlaced: number;
    totalOffersAccepted: number;
    totalOffersRejected: number;
    totalWithdrawn: number;
    // New detailed metrics
    totalUnderReview?: number;
    totalSubmittedToManager?: number;
    totalRejectedByRecruiter?: number;
    totalRejectedByManager?: number;
    totalInterviewingWithRecruiter?: number;
    totalInterviewingWithManager?: number;
    totalShortlistedByRecruiter?: number;
    totalShortlistedByManager?: number;
    // Application sources
    totalInvitedApplications?: number;
    totalDirectApplications?: number;
    // Offer details
    totalOffersPending?: number;
    totalOffersExpired?: number;
    totalOffersRevoked?: number;
  };
  percentages: {
    hireRate: string;
    rejectionRate: string;
    shortlistRate: string;
    offerAcceptanceRate: string;
    withdrawalRate: string;
  };
}

interface TimeToHireData {
  timeStats: {
    averageTimeToHire: number;
    minTimeToHire: number;
    maxTimeToHire: number;
    medianTimeToHire: number;
  };
  timeToHireData: Array<{
    jobTitle: string;
    companyName: string;
    daysToHire: number;
    dateApplied: string;
    dateHired: string;
  }>;
  totalHired: number;
}

interface ConversionFunnel {
  funnel: Array<{
    stage: string;
    count: number;
    percentage: number;
  }>;
  dropOffAnalysis: Array<{
    from: string;
    to: string;
    dropOff: number;
    dropOffPercentage: number;
  }>;
}

interface PerformanceSummary {
  summary: {
    totalApplications: number;
    totalHired: number;
    hireRate: string;
    averageTimeToHire: number;
    offerAcceptanceRate: string;
    topBottleneck: string;
  };
}

interface JobPerformance {
  jobPerformance: Array<{
    jobId: string;
    jobTitle: string;
    companyName: string;
    totalApplications: number;
    totalHired: number;
    hireRate: number;
    averageTimeToHire: number;
    offerAcceptanceRate: number;
  }>;
  bestPerforming: {
    byHireRate: any;
    byTimeToHire: any;
    byVolume: any;
  };
}

interface ApplicationsTrend {
  trend: Array<{ period: string; applications: number; hired: number }>;
  groupBy: 'week' | 'month';
}

interface StageDuration {
  stageDurations: Array<{ stage: string; avgDays: number; sampleSize: number }>;
}

interface ScreeningDistribution {
  distribution: Array<{ range: string; count: number; percentage: number }>;
  totalScreened: number;
  avgScore: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const ReportingDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | undefined>(undefined);
  const [jobs, setJobs] = useState<any[]>([]);
  const [downloading, setDownloading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [hiringMetrics, setHiringMetrics] = useState<HiringMetrics | null>(null);
  const [timeToHire, setTimeToHire] = useState<TimeToHireData | null>(null);
  const [conversionFunnel, setConversionFunnel] = useState<ConversionFunnel | null>(null);
  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummary | null>(null);
  const [jobPerformance, setJobPerformance] = useState<JobPerformance | null>(null);
  const [applicationsTrend, setApplicationsTrend] = useState<ApplicationsTrend | null>(null);
  const [stageDuration, setStageDuration] = useState<StageDuration | null>(null);
  const [screeningDistribution, setScreeningDistribution] = useState<ScreeningDistribution | null>(null);
  const [trendGroupBy, setTrendGroupBy] = useState<'week' | 'month'>('week');
  
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axiosInstant.get('/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchAllReports = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.append('startDate', dateRange[0].toISOString());
        params.append('endDate', dateRange[1].toISOString());
      }
      if (selectedJob) {
        params.append('jobId', selectedJob);
      }

      const trendParams = new URLSearchParams(params);
      trendParams.set('groupBy', trendGroupBy);

      const [metricsRes, timeRes, funnelRes, summaryRes, jobPerfRes, trendRes, stageRes, screeningRes] = await Promise.all([
        axiosInstant.get(`/reporting/hiring-metrics?${params.toString()}`),
        axiosInstant.get(`/reporting/time-to-hire?${params.toString()}`),
        axiosInstant.get(`/reporting/conversion-funnel?${params.toString()}`),
        axiosInstant.get(`/reporting/performance-summary?${params.toString()}`),
        axiosInstant.get(`/reporting/job-performance?${params.toString()}`),
        axiosInstant.get(`/reporting/applications-trend?${trendParams.toString()}`),
        axiosInstant.get(`/reporting/stage-duration?${params.toString()}`),
        axiosInstant.get(`/reporting/screening-distribution?${params.toString()}`),
      ]);

      setHiringMetrics(metricsRes.data);
      setTimeToHire(timeRes.data);
      setConversionFunnel(funnelRes.data);
      setPerformanceSummary(summaryRes.data);
      setJobPerformance(jobPerfRes.data);
      setApplicationsTrend(trendRes.data);
      setStageDuration(stageRes.data);
      setScreeningDistribution(screeningRes.data);
    } catch (error) {
      setFetchError('Failed to load analytics. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = () => {
    fetchAllReports();
  };

  const clearFilters = () => {
    setDateRange(null);
    setSelectedJob(undefined);
    // This will trigger fetchAllReports automatically due to useEffect dependency
  };

  // Auto-fetch when filters or groupBy change
  useEffect(() => {
    fetchAllReports();
  }, [dateRange, selectedJob, trendGroupBy]);

  const formatChartData = () => {
    if (!hiringMetrics) return [];
    
    return [
      { name: 'Applications', value: hiringMetrics.metrics.totalApplications, color: '#0088FE' },
      { name: 'Hired', value: hiringMetrics.metrics.totalHired, color: '#00C49F' },
      { name: 'Rejected', value: hiringMetrics.metrics.totalRejected, color: '#FF8042' },
      { name: 'Pending', value: hiringMetrics.metrics.totalPending, color: '#FFBB28' },
      { name: 'Shortlisted', value: hiringMetrics.metrics.totalShortlisted, color: '#8884d8' }
    ];
  };

  const generateReportTitle = () => {
    const selectedJobTitle = selectedJob ? jobs.find(job => job._id === selectedJob)?.positionTitle || jobs.find(job => job._id === selectedJob)?.title : null;
    const selectedCompany = selectedJob ? jobs.find(job => job._id === selectedJob)?.companyName : null;
    
    if (selectedJobTitle && selectedCompany) {
      return `Hiring Analytics Report - ${selectedJobTitle} at ${selectedCompany}`;
    } else if (selectedJobTitle) {
      return `Hiring Analytics Report - ${selectedJobTitle}`;
    } else {
      return 'Hiring Analytics Report - All Jobs Summary';
    }
  };

  const downloadAsPDF = async () => {
    if (!reportRef.current) return;
    
    setDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 295;
      const margin = 20;
      
      // Add title page
      pdf.setFontSize(24);
      pdf.setTextColor(31, 78, 121); // #1f4e79
      
      // Center the title
      const title = generateReportTitle();
      const titleWidth = pdf.getStringUnitWidth(title) * 24 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 50);
      
      // Add subtitle with date and period
      pdf.setFontSize(14);
      pdf.setTextColor(102, 102, 102); // #666
      const subtitle = `Generated on: ${new Date().toLocaleDateString()}`;
      const subtitleWidth = pdf.getStringUnitWidth(subtitle) * 14 / pdf.internal.scaleFactor;
      const subtitleX = (pageWidth - subtitleWidth) / 2;
      pdf.text(subtitle, subtitleX, 70);
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        const period = `Report Period: ${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`;
        const periodWidth = pdf.getStringUnitWidth(period) * 14 / pdf.internal.scaleFactor;
        const periodX = (pageWidth - periodWidth) / 2;
        pdf.text(period, periodX, 85);
      }
      
      // Add company logo area or branding
      pdf.setFontSize(12);
      pdf.setTextColor(153, 153, 153); // #999
      const brandText = 'ChronoFlow Hiring Analytics';
      const brandWidth = pdf.getStringUnitWidth(brandText) * 12 / pdf.internal.scaleFactor;
      const brandX = (pageWidth - brandWidth) / 2;
      pdf.text(brandText, brandX, pageHeight - 30);
      
      // Add a new page for the actual report content
      pdf.addPage();
      
      // Capture the dashboard content
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;
      
      // Add header to each page with content
      const addPageHeader = (pageNum: number) => {
        pdf.setFontSize(16);
        pdf.setTextColor(31, 78, 121);
        pdf.text(title, margin, 15);
        
        pdf.setFontSize(10);
        pdf.setTextColor(102, 102, 102);
        pdf.text(`Page ${pageNum}`, pageWidth - margin - 20, 15);
        
        // Add a line under the header
        pdf.setDrawColor(31, 78, 121);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 20, pageWidth - margin, 20);
      };
      
      let pageNumber = 1;
      addPageHeader(pageNumber);
      
      // Add the main content image
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - position - margin);
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pageNumber++;
        addPageHeader(pageNumber);
        pdf.addImage(imgData, 'PNG', margin, margin + 10, imgWidth, imgHeight);
        heightLeft -= (pageHeight - margin * 2 - 10);
      }
      
      pdf.save(`${generateReportTitle()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const downloadAsPowerPoint = async () => {
    setDownloading(true);
    try {
      // Create a comprehensive HTML report that can be easily imported into PowerPoint
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${generateReportTitle()}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 40px; background: #f5f5f5; }
            .slide { background: white; padding: 40px; margin: 40px 0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); page-break-after: always; }
            .slide h1 { color: #1f4e79; font-size: 32px; margin-bottom: 20px; text-align: center; }
            .slide h2 { color: #1f4e79; font-size: 24px; margin-bottom: 15px; border-bottom: 2px solid #1f4e79; padding-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { padding: 12px; text-align: left; border: 1px solid #ddd; }
            th { background-color: #1f4e79; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f8f9fa; }
            .metric-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 20px 0; }
            .metric-card { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 8px; text-align: center; }
            .metric-value { font-size: 32px; font-weight: bold; margin: 10px 0; }
            .metric-label { font-size: 14px; opacity: 0.9; }
            .funnel-stage { background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%); color: white; padding: 15px; margin: 10px 0; border-radius: 8px; text-align: center; }
            .stage-title { font-size: 18px; font-weight: bold; }
            .stage-count { font-size: 24px; margin: 5px 0; }
            .stage-percent { font-size: 14px; opacity: 0.9; }
            .summary { background: #e6f7ff; padding: 20px; border-radius: 8px; margin: 20px 0; }
            @media print { .slide { page-break-after: always; } }
          </style>
        </head>
        <body>
      `;

      // Title slide
      htmlContent += `
        <div class="slide">
          <h1>${generateReportTitle()}</h1>
          <div style="text-align: center; font-size: 18px; color: #666; margin-top: 40px;">
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
            ${dateRange && dateRange[0] && dateRange[1] ? 
              `<p>Report Period: ${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}</p>` : 
              ''
            }
          </div>
        </div>
      `;

      // Key metrics slide
      if (hiringMetrics) {
        htmlContent += `
          <div class="slide">
            <h2>Key Hiring Metrics</h2>
            <div class="metric-grid">
              <div class="metric-card">
                <div class="metric-value">${hiringMetrics.metrics.totalApplications}</div>
                <div class="metric-label">Total Applications</div>
              </div>
              <div class="metric-card">
                <div class="metric-value">${hiringMetrics.metrics.totalHired}</div>
                <div class="metric-label">Total Hired</div>
              </div>
              <div class="metric-card">
                <div class="metric-value">${hiringMetrics.percentages.hireRate}%</div>
                <div class="metric-label">Hire Rate</div>
              </div>
            </div>
            
            <table>
              <thead>
                <tr><th>Metric</th><th>Value</th><th>Percentage</th></tr>
              </thead>
              <tbody>
                <tr><td>Total Applications</td><td>${hiringMetrics.metrics.totalApplications}</td><td>100%</td></tr>
                <tr><td>Total Hired</td><td>${hiringMetrics.metrics.totalHired}</td><td>${hiringMetrics.percentages.hireRate}%</td></tr>
                <tr><td>Total Rejected</td><td>${hiringMetrics.metrics.totalRejected}</td><td>${hiringMetrics.percentages.rejectionRate}%</td></tr>
                <tr><td>Offers Placed</td><td>${hiringMetrics.metrics.totalOffersPlaced}</td><td>-</td></tr>
                <tr><td>Offers Accepted</td><td>${hiringMetrics.metrics.totalOffersAccepted}</td><td>${hiringMetrics.percentages.offerAcceptanceRate}%</td></tr>
                <tr><td>Invite-based Applications</td><td>${hiringMetrics.metrics.totalInvitedApplications || 0}</td><td>${hiringMetrics.metrics.totalApplications > 0 ? Math.round(((hiringMetrics.metrics.totalInvitedApplications || 0) / hiringMetrics.metrics.totalApplications) * 100) : 0}%</td></tr>
                <tr><td>Direct Applications</td><td>${hiringMetrics.metrics.totalDirectApplications || 0}</td><td>${hiringMetrics.metrics.totalApplications > 0 ? Math.round(((hiringMetrics.metrics.totalDirectApplications || 0) / hiringMetrics.metrics.totalApplications) * 100) : 0}%</td></tr>
                <tr><td>Average Time to Hire</td><td>${timeToHire?.timeStats.averageTimeToHire || 'N/A'} days</td><td>-</td></tr>
              </tbody>
            </table>
          </div>
        `;
      }

      // Conversion funnel slide
      if (conversionFunnel?.funnel) {
        htmlContent += `
          <div class="slide">
            <h2>Candidate Conversion Funnel</h2>
            <div style="margin: 30px 0;">
        `;
        
        conversionFunnel.funnel.forEach((stage, index) => {
          const colors = ['#52c41a', '#1890ff', '#faad14', '#ff7a45', '#f759ab', '#13c2c2'];
          const color = colors[index % colors.length];
          htmlContent += `
            <div class="funnel-stage" style="background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%);">
              <div class="stage-title">${stage.stage}</div>
              <div class="stage-count">${stage.count} candidates</div>
              <div class="stage-percent">${stage.percentage}% of total</div>
            </div>
          `;
        });

        htmlContent += `
            </div>
            <table>
              <thead>
                <tr><th>Stage</th><th>Candidates</th><th>Percentage</th></tr>
              </thead>
              <tbody>
        `;
        
        conversionFunnel.funnel.forEach(stage => {
          htmlContent += `<tr><td>${stage.stage}</td><td>${stage.count}</td><td>${stage.percentage}%</td></tr>`;
        });

        htmlContent += `
              </tbody>
            </table>
          </div>
        `;
      }

      // Job performance slide
      if (jobPerformance?.jobPerformance && jobPerformance.jobPerformance.length > 0) {
        htmlContent += `
          <div class="slide">
            <h2>Job Performance Comparison</h2>
            <table>
              <thead>
                <tr><th>Job Title</th><th>Company</th><th>Applications</th><th>Hired</th><th>Hire Rate</th></tr>
              </thead>
              <tbody>
        `;
        
        jobPerformance.jobPerformance.slice(0, 10).forEach(job => {
          htmlContent += `
            <tr>
              <td>${job.jobTitle}</td>
              <td>${job.companyName}</td>
              <td>${job.totalApplications}</td>
              <td>${job.totalHired}</td>
              <td>${job.hireRate.toFixed(1)}%</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
          </div>
        `;
      }

      htmlContent += `
        </body>
        </html>
      `;

      // Create and download HTML file
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      saveAs(blob, `${generateReportTitle()}.html`);
      
      // Show instructions to user
      setTimeout(() => {
        alert('HTML report downloaded! To convert to PowerPoint:\n1. Open the HTML file in your browser\n2. Print the page and save as PDF\n3. Import PDF into PowerPoint\n\nOr copy tables directly from the HTML file into PowerPoint slides.');
      }, 500);

    } catch (error) {
      console.error('Error generating PowerPoint:', error);
    } finally {
      setDownloading(false);
    }
  };

  const downloadMenu = (
    <Menu
      items={[
        {
          key: 'pdf',
          icon: <FilePdfOutlined />,
          label: 'Download as PDF',
          onClick: downloadAsPDF
        },
        {
          key: 'ppt',
          icon: <FileOutlined />,
          label: 'Download as PowerPoint',
          onClick: downloadAsPowerPoint
        }
      ]}
    />
  );

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <Title level={1} style={{ marginBottom: '24px', fontSize: '32px' }}>Hiring Analytics Dashboard</Title>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Filters:</Text>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
            />
          </Col>
          <Col>
            <Select
              placeholder="Select Job"
              style={{ width: 220 }}
              value={selectedJob}
              onChange={setSelectedJob}
              allowClear
            >
              {jobs.map((job) => (
                <Option key={job._id} value={job._id}>
                  {job.positionTitle || job.title} - {job.companyName}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </Col>
          <Col style={{ marginLeft: 'auto' }}>
            <Dropdown overlay={downloadMenu} placement="bottomRight">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={downloading}
                disabled={downloading}
              >
                Download Report
              </Button>
            </Dropdown>
          </Col>
        </Row>
      </Card>

      {fetchError && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px', color: '#cf1322', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined />
          {fetchError}
          <Button size="small" style={{ marginLeft: 'auto' }} onClick={fetchAllReports}>Retry</Button>
        </div>
      )}

      {/* Currently Viewing Section */}
      {selectedJob && (
        <Card style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
          <div style={{ color: 'white', textAlign: 'center' }}>
            <Title level={3} style={{ color: 'white', margin: 0, marginBottom: '10px', fontSize: '26px' }}>
              Currently Viewing Analytics
            </Title>
            <div style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '6px' }}>
              {jobs.find(job => job._id === selectedJob)?.positionTitle || jobs.find(job => job._id === selectedJob)?.title || 'Job Title'}
            </div>
            <div style={{ fontSize: '18px', opacity: 0.9 }}>
              {jobs.find(job => job._id === selectedJob)?.companyName || 'Company Name'}
            </div>
            <div style={{ fontSize: '15px', opacity: 0.7, marginTop: '8px' }}>
              {dateRange && dateRange[0] && dateRange[1]
                ? `Date Range: ${dateRange[0].format('MMM DD, YYYY')} - ${dateRange[1].format('MMM DD, YYYY')}`
                : 'All Time Data'
              }
            </div>
          </div>
        </Card>
      )}

      {!selectedJob && (
        <Card style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)', border: 'none' }}>
          <div style={{ color: 'white', textAlign: 'center' }}>
            <Title level={3} style={{ color: 'white', margin: 0, marginBottom: '10px', fontSize: '26px' }}>
              Overall Analytics Dashboard
            </Title>
            <div style={{ fontSize: '18px', opacity: 0.9 }}>
              Viewing analytics for all jobs and candidates
            </div>
            <div style={{ fontSize: '15px', opacity: 0.7, marginTop: '8px' }}>
              {dateRange && dateRange[0] && dateRange[1]
                ? `Date Range: ${dateRange[0].format('MMM DD, YYYY')} - ${dateRange[1].format('MMM DD, YYYY')}`
                : 'All Time Data'
              }
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading analytics...</div>
        </div>
      ) : (
        <div ref={reportRef} style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '8px' }}>
          {/* Key Metrics Summary */}
          {hiringMetrics && (
            <>
              <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <UserOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {hiringMetrics.metrics.totalApplications}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Total Applications</div>
                    </div>
                  </Card>
                </Col>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {hiringMetrics.metrics.totalHired}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Total Hired</div>
                    </div>
                  </Card>
                </Col>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <TrophyOutlined style={{ fontSize: '24px', color: '#faad14', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {hiringMetrics.percentages.hireRate}%
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Hire Rate</div>
                    </div>
                  </Card>
                </Col>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <ClockCircleOutlined style={{ fontSize: '24px', color: '#722ed1', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {timeToHire?.timeStats.averageTimeToHire || 0}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Avg. Days to Hire</div>
                    </div>
                  </Card>
                </Col>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <CheckCircleOutlined style={{ fontSize: '24px', color: '#13c2c2', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {hiringMetrics.percentages.offerAcceptanceRate}%
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Offer Acceptance</div>
                    </div>
                  </Card>
                </Col>
                <Col span={4}>
                  <Card>
                    <div style={{ textAlign: 'center' }}>
                      <CalendarOutlined style={{ fontSize: '24px', color: '#ff7a45', marginBottom: '8px' }} />
                      <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                        {hiringMetrics.metrics.totalInterviewing}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Interviewing</div>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Detailed Metrics */}
              <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col span={12}>
                  <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Detailed Application Status</span>}>
                    <Row gutter={8}>
                      <Col span={12}>
                        <div style={{ padding: '8px', textAlign: 'center', background: '#f6ffed', borderRadius: '4px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                            {hiringMetrics.metrics.totalUnderReview || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Under Review</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ padding: '8px', textAlign: 'center', background: '#e6f7ff', borderRadius: '4px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {hiringMetrics.metrics.totalSubmittedToManager || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>To Manager</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ padding: '8px', textAlign: 'center', background: '#fff7e6', borderRadius: '4px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                            {hiringMetrics.metrics.totalInterviewingWithRecruiter || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Interview (Recruiter)</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ padding: '8px', textAlign: 'center', background: '#fff1f0', borderRadius: '4px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                            {hiringMetrics.metrics.totalInterviewingWithManager || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Interview (Manager)</div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Application Sources</span>}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: 'white' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                            {hiringMetrics.metrics.totalInvitedApplications || 0}
                          </div>
                          <div style={{ fontSize: '16px', opacity: 0.9 }}>Invited Candidates</div>
                          <div style={{ fontSize: '15px', opacity: 0.7 }}>
                            {hiringMetrics.metrics.totalApplications > 0
                              ? Math.round(((hiringMetrics.metrics.totalInvitedApplications || 0) / hiringMetrics.metrics.totalApplications) * 100)
                              : 0}% of total
                          </div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', borderRadius: '8px', color: '#8b4513' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                            {hiringMetrics.metrics.totalDirectApplications || 0}
                          </div>
                          <div style={{ fontSize: '16px', opacity: 0.9 }}>Direct Applications</div>
                          <div style={{ fontSize: '15px', opacity: 0.7 }}>
                            {hiringMetrics.metrics.totalApplications > 0
                              ? Math.round(((hiringMetrics.metrics.totalDirectApplications || 0) / hiringMetrics.metrics.totalApplications) * 100)
                              : 0}% of total
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Offer Management */}
              <Row gutter={16} style={{ marginBottom: '28px' }}>
                <Col span={24}>
                  <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Offer Management</span>}>
                    <Row gutter={16}>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#f6ffed', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                            {hiringMetrics.metrics.totalOffersPlaced}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Total Offers</div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#e6f7ff', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {hiringMetrics.metrics.totalOffersAccepted}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Accepted</div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#fff1f0', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                            {hiringMetrics.metrics.totalOffersRejected}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Rejected</div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#fff7e6', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                            {hiringMetrics.metrics.totalOffersPending || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Pending</div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#f9f0ff', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                            {hiringMetrics.metrics.totalOffersExpired || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Expired</div>
                        </div>
                      </Col>
                      <Col span={4}>
                        <div style={{ textAlign: 'center', padding: '16px', background: '#f6f6f6', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#666' }}>
                            {hiringMetrics.metrics.totalOffersRevoked || 0}
                          </div>
                          <div style={{ fontSize: '18px', color: '#666' }}>Revoked</div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          <Row gutter={16} style={{ marginBottom: '24px' }}>
            {/* Hiring Overview Chart */}
            <Col span={12}>
              <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Hiring Overview</span>}>
                <ResponsiveContainer width="100%" height={580}>
                  <PieChart>
                    <Pie
                      data={formatChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={{ stroke: '#8884d8', strokeWidth: 1.5 }}
                      label={(props: any) => {
                        const { cx, cy, midAngle, outerRadius, name, value, percent, index } = props;
                        const RADIAN = Math.PI / 180;

                        const baseRadius = outerRadius + 65;
                        let x = cx + baseRadius * Math.cos(-midAngle * RADIAN);
                        let y = cy + baseRadius * Math.sin(-midAngle * RADIAN);

                        const data = formatChartData();
                        let sameValueOffset = 0;
                        data.forEach((item, i) => {
                          if (i < index && item.value === value) {
                            sameValueOffset += 22;
                          }
                        });
                        if (sameValueOffset > 0) y += sameValueOffset;

                        return (
                          <text
                            x={x}
                            y={y}
                            fill="#333"
                            textAnchor={x > cx ? 'start' : 'end'}
                            dominantBaseline="central"
                            style={{ fontSize: '15px', fontWeight: 'bold' }}
                          >
                            {`${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                          </text>
                        );
                      }}
                      outerRadius={155}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: '15px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Time to Hire Distribution */}
            <Col span={12}>
              <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Time to Hire Analysis</span>}>
                {timeToHire && (
                  <div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                            {timeToHire.timeStats.averageTimeToHire}
                          </div>
                          <div style={{ color: '#666', fontSize: '16px' }}>Average Days</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#52c41a' }}>
                            {timeToHire.timeStats.medianTimeToHire}
                          </div>
                          <div style={{ color: '#666', fontSize: '16px' }}>Median Days</div>
                        </div>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#52c41a' }}>
                            {timeToHire.timeStats.minTimeToHire}
                          </div>
                          <div style={{ color: '#666', fontSize: '16px' }}>Fastest</div>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: '8px' }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff4d4f' }}>
                            {timeToHire.timeStats.maxTimeToHire}
                          </div>
                          <div style={{ color: '#666', fontSize: '16px' }}>Longest</div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Conversion Funnel */}
          {conversionFunnel && conversionFunnel.funnel && conversionFunnel.funnel.length > 0 && (
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={24}>
                <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Candidate Conversion Funnel</span>}>
                  {/* Modern Funnel Visualization */}
                  <div style={{ padding: '30px 0', background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)', borderRadius: '12px', display: 'flex', justifyContent: 'center' }}>
                    <svg width="800" height="450" style={{ overflow: 'visible' }}>
                      <defs>
                        <linearGradient id="funnelGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#52c41a" />
                          <stop offset="100%" stopColor="#389e0d" />
                        </linearGradient>
                        <linearGradient id="funnelGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#1890ff" />
                          <stop offset="100%" stopColor="#096dd9" />
                        </linearGradient>
                        <linearGradient id="funnelGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#faad14" />
                          <stop offset="100%" stopColor="#d48806" />
                        </linearGradient>
                        <linearGradient id="funnelGradient4" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ff7a45" />
                          <stop offset="100%" stopColor="#d4380d" />
                        </linearGradient>
                        <linearGradient id="funnelGradient5" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#f759ab" />
                          <stop offset="100%" stopColor="#c41d7f" />
                        </linearGradient>
                        <linearGradient id="funnelGradient6" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#13c2c2" />
                          <stop offset="100%" stopColor="#08979c" />
                        </linearGradient>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="rgba(0,0,0,0.15)"/>
                        </filter>
                      </defs>
                      
                      {conversionFunnel.funnel.map((stage, index) => {
                        const centerX = 400;
                        const stageHeight = 55;
                        const y = index * 70 + 20;

                        // Every bar is the same fixed width. Sizing it by count used to
                        // shrink later stages down to a 50px floor while their label,
                        // count and percentage text stayed full-size and centered - the
                        // text spilled out past the bar on any stage with a big drop-off
                        // or a longer stage name, which is what looked broken. The
                        // numbers already say how each stage compares; the bar itself
                        // doesn't need to resize to carry that.
                        const currentWidth = 600;

                        const leftX = centerX - currentWidth / 2;
                        
                        return (
                          <g key={stage.stage}>
                            {/* Funnel Section with Rounded Corners */}
                            <rect
                              x={leftX}
                              y={y}
                              width={currentWidth}
                              height={stageHeight}
                              rx="8"
                              ry="8"
                              fill={`url(#funnelGradient${(index % 6) + 1})`}
                              stroke="rgba(255,255,255,0.3)"
                              strokeWidth="2"
                              filter="url(#shadow)"
                              style={{ cursor: 'pointer' }}
                            />
                            
                            {/* Stage Label */}
                            <text
                              x={centerX}
                              y={y + stageHeight / 2 - 8}
                              textAnchor="middle"
                              fill="white"
                              fontSize="16"
                              fontWeight="600"
                              fontFamily="Arial, sans-serif"
                            >
                              {stage.stage}
                            </text>
                            
                            {/* Count and Percentage */}
                            <text
                              x={centerX}
                              y={y + stageHeight / 2 + 8}
                              textAnchor="middle"
                              fill="rgba(255,255,255,0.9)"
                              fontSize="16"
                              fontWeight="500"
                              fontFamily="Arial, sans-serif"
                            >
                              {stage.count} candidates
                            </text>
                            <text
                              x={centerX}
                              y={y + stageHeight / 2 + 24}
                              textAnchor="middle"
                              fill="rgba(255,255,255,0.8)"
                                  fontSize="16"
                              fontWeight="400"
                              fontFamily="Arial, sans-serif"
                            >
                              ({stage.percentage}% of total)
                            </text>
                            
                            {/* Connecting Arrow */}
                            {index < conversionFunnel.funnel.length - 1 && (
                              <g>
                                <line
                                  x1={centerX}
                                  y1={y + stageHeight + 5}
                                  x2={centerX}
                                  y2={y + stageHeight + 12}
                                  stroke="#bfbfbf"
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                                <polygon
                                  points={`${centerX - 5},${y + stageHeight + 12} ${centerX + 5},${y + stageHeight + 12} ${centerX},${y + stageHeight + 18}`}
                                  fill="#bfbfbf"
                                />
                              </g>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                  
                  {/* Statistics Row */}
                  <Row gutter={16} style={{ marginTop: '20px', textAlign: 'center' }}>
                    {conversionFunnel.funnel.map((stage, index) => (
                      <Col span={Math.floor(24 / conversionFunnel.funnel.length)} key={stage.stage}>
                        <div style={{ 
                          padding: '16px', 
                          border: '1px solid #f0f0f0', 
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)'
                        }}>
                          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                            {stage.count}
                          </div>
                          <div style={{ fontSize: '17px', color: '#666', marginTop: '4px' }}>
                            {stage.stage}
                          </div>
                          <div style={{ fontSize: '17px', color: '#52c41a', fontWeight: 'bold' }}>
                            {stage.percentage}%
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  
                  {/* Drop-off Analysis */}
                  {conversionFunnel.dropOffAnalysis && conversionFunnel.dropOffAnalysis.length > 0 && (
                    <div style={{ marginTop: '30px' }}>
                      <Title level={4}>Drop-off Analysis</Title>
                      <div style={{ background: '#fafafa', padding: '16px', borderRadius: '8px' }}>
                        {conversionFunnel.dropOffAnalysis.map((dropOff, index) => {
                          const isHighDropOff = dropOff.dropOffPercentage > 50;
                          return (
                            <div key={index} style={{
                              marginBottom: '12px',
                              padding: '12px',
                              background: isHighDropOff ? '#fff2f0' : '#f6ffed',
                              border: `1px solid ${isHighDropOff ? '#ffccc7' : '#d9f7be'}`,
                              borderRadius: '6px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                  <strong>{dropOff.from}</strong> → <strong>{dropOff.to}</strong>
                                </Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{
                                    color: isHighDropOff ? '#ff4d4f' : '#faad14',
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                  }}>
                                    -{dropOff.dropOff} candidates
                                  </span>
                                  <span style={{
                                    background: isHighDropOff ? '#ff4d4f' : '#faad14',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    fontWeight: 'bold'
                                  }}>
                                    {dropOff.dropOffPercentage}% drop
                                  </span>
                                  {isHighDropOff && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          )}

          {/* Job Performance Comparison */}
          {jobPerformance && jobPerformance.jobPerformance.length > 0 && (
            <Row gutter={16} style={{ marginBottom: '24px' }}>
              <Col span={24}>
                  <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Job Performance Comparison</span>}>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={jobPerformance.jobPerformance.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="jobTitle"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 13 }}
                      />
                      <YAxis tick={{ fontSize: 13 }} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'totalApplications') return [`${value} applications`, 'Applications'];
                          if (name === 'totalHired') return [`${value} hired`, 'Hired'];
                          if (name === 'hireRate') return [`${value}%`, 'Hire Rate'];
                          return [value, name];
                        }}
                        contentStyle={{ fontSize: '14px' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '14px' }} />
                      <Bar dataKey="totalApplications" fill="#8884d8" name="Applications" />
                      <Bar dataKey="totalHired" fill="#82ca9d" name="Hired" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>
          )}

          {/* Best Performing Jobs */}
          {jobPerformance?.bestPerforming && (
            <Row gutter={16}>
              <Col span={8}>
                <Card title={<span style={{ fontSize: '18px', fontWeight: '600' }}>Highest Hire Rate</span>} size="small">
                  {jobPerformance.bestPerforming.byHireRate ? (
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                        {jobPerformance.bestPerforming.byHireRate.jobTitle}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>
                        {jobPerformance.bestPerforming.byHireRate.companyName}
                      </div>
                      <div style={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}>
                        {jobPerformance.bestPerforming.byHireRate.hireRate.toFixed(1)}%
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">No data available</Text>
                  )}
                </Card>
              </Col>
              <Col span={8}>
                <Card title={<span style={{ fontSize: '18px', fontWeight: '600' }}>Fastest Time to Hire</span>} size="small">
                  {jobPerformance.bestPerforming.byTimeToHire ? (
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                        {jobPerformance.bestPerforming.byTimeToHire.jobTitle}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>
                        {jobPerformance.bestPerforming.byTimeToHire.companyName}
                      </div>
                      <div style={{ color: '#1890ff', fontSize: '20px', fontWeight: 'bold' }}>
                        {jobPerformance.bestPerforming.byTimeToHire.averageTimeToHire} days
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">No data available</Text>
                  )}
                </Card>
              </Col>
              <Col span={8}>
                <Card title={<span style={{ fontSize: '18px', fontWeight: '600' }}>Highest Volume</span>} size="small">
                  {jobPerformance.bestPerforming.byVolume ? (
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
                        {jobPerformance.bestPerforming.byVolume.jobTitle}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>
                        {jobPerformance.bestPerforming.byVolume.companyName}
                      </div>
                      <div style={{ color: '#722ed1', fontSize: '20px', fontWeight: 'bold' }}>
                        {jobPerformance.bestPerforming.byVolume.totalApplications} applications
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">No data available</Text>
                  )}
                </Card>
              </Col>
            </Row>
          )}

          {/* Applications Over Time */}
          {applicationsTrend && applicationsTrend.trend.length > 0 && (
            <Row gutter={16} style={{ marginBottom: '24px', marginTop: '24px' }}>
              <Col span={24}>
                <Card
                  title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Applications Over Time</span>}
                  extra={
                    <Select
                      value={trendGroupBy}
                      onChange={(v: 'week' | 'month') => setTrendGroupBy(v)}
                      style={{ width: 110 }}
                    >
                      <Option value="week">By Week</Option>
                      <Option value="month">By Month</Option>
                    </Select>
                  }
                >
                  <ResponsiveContainer width="100%" height={340}>
                    <LineChart data={applicationsTrend.trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" tick={{ fontSize: 14 }} angle={-30} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 14 }} />
                      <Tooltip contentStyle={{ fontSize: '16px' }} />
                      <Legend wrapperStyle={{ fontSize: '16px' }} />
                      <Line type="monotone" dataKey="applications" stroke="#1890ff" strokeWidth={2} dot={{ r: 4 }} name="Applications" />
                      <Line type="monotone" dataKey="hired" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} name="Hired" />
                    </LineChart>
                  </ResponsiveContainer>
                  {applicationsTrend.trend.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No data for selected period</div>
                  )}
                </Card>
              </Col>
            </Row>
          )}

          {/* Stage Duration + Screening Distribution */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            {/* Average Time per Stage */}
            {stageDuration && stageDuration.stageDurations.some(s => s.sampleSize > 0) && (
              <Col span={12}>
                <Card title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Avg. Days per Stage</span>}>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      layout="vertical"
                      data={stageDuration.stageDurations.filter(s => s.sampleSize > 0)}
                      margin={{ left: 20, right: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 14 }} unit=" days" />
                      <YAxis type="category" dataKey="stage" tick={{ fontSize: 16 }} width={200} />
                      <Tooltip
                        formatter={(value: any, name: any, props: any) => [
                          `${value} days (${props.payload.sampleSize} applications)`,
                          'Avg. Time',
                        ]}
                        contentStyle={{ fontSize: '16px' }}
                      />
                      <Bar dataKey="avgDays" fill="#722ed1" radius={[0, 4, 4, 0]}>
                        {stageDuration.stageDurations
                          .filter(s => s.sampleSize > 0)
                          .map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {stageDuration.stageDurations.filter(s => s.sampleSize > 0).map((s, i) => (
                      <div key={i} style={{ background: '#f6f9fc', borderRadius: '6px', padding: '8px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS[i % COLORS.length] }}>{s.avgDays}d</div>
                        <div style={{ fontSize: '18px', color: '#666' }}>{s.stage}</div>
                        <div style={{ fontSize: '16px', color: '#999' }}>{s.sampleSize} samples</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            )}

            {/* Screening Score Distribution */}
            {screeningDistribution && screeningDistribution.totalScreened > 0 && (
              <Col span={stageDuration && stageDuration.stageDurations.some(s => s.sampleSize > 0) ? 12 : 24}>
                <Card
                  title={<span style={{ fontSize: '20px', fontWeight: '600' }}>Screening Score Distribution</span>}
                  extra={
                    <span style={{ fontSize: '16px', color: '#666' }}>
                      {screeningDistribution.totalScreened} screened · Avg: <strong style={{ color: '#1890ff' }}>{screeningDistribution.avgScore}</strong>
                    </span>
                  }
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={screeningDistribution.distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" tick={{ fontSize: 15 }} />
                      <YAxis tick={{ fontSize: 14 }} />
                      <Tooltip
                        formatter={(value: any, _: any, props: any) => [
                          `${value} candidates (${props.payload.percentage}%)`,
                          'Count',
                        ]}
                        contentStyle={{ fontSize: '16px' }}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {screeningDistribution.distribution.map((entry, i) => {
                          const color = entry.range === '81–100' ? '#52c41a'
                            : entry.range === '61–80' ? '#1890ff'
                            : entry.range === '41–60' ? '#faad14'
                            : entry.range === '21–40' ? '#ff7a45'
                            : '#ff4d4f';
                          return <Cell key={i} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '8px' }}>
                    {screeningDistribution.distribution.map((b, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>{b.count}</div>
                        <div style={{ fontSize: '16px', color: '#666' }}>{b.range}</div>
                        <div style={{ fontSize: '16px', color: '#52c41a', fontWeight: 'bold' }}>{b.percentage}%</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            )}
          </Row>

        </div>
      )}
    </div>
  );
};

export default ReportingDashboard;
